import {
  Component,
  ContentChildren,
  QueryList,
  AfterViewInit,
  ElementRef,
  ViewChild,
  PLATFORM_ID,
  Inject,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import KeenSlider, { KeenSliderInstance, KeenSliderOptions } from "keen-slider";
import { isPlatformBrowser, NgIf } from "@angular/common";
import { environment } from "../../../environments/environment";

@Component({
  selector: "glo-carousel-arrow",
  templateUrl: "./carousel-arrow.component.html",
  styleUrls: ["./carousel-arrow.component.scss"],
  imports: [NgIf],
})
export class GloCarouselArrowComponent implements AfterViewInit, OnDestroy {
  @ViewChild("sliderRef", { static: true }) sliderRef!: ElementRef<HTMLElement>;
  @ContentChildren("carouselSlide", { descendants: true, read: ElementRef })
  slidesContent!: QueryList<ElementRef>;

  assetUrl = environment.assetBaseUrl;

  slider?: KeenSliderInstance;
  currentSlide = 0;
  slidesPerView = 4;
  private isInitialized = false;
  private updateTimeout?: ReturnType<typeof setTimeout>;

  ksOptions: KeenSliderOptions = {
    loop: true,
    renderMode: "performance",
    rubberband: false,
    slides: {
      perView: 4,
      spacing: 16,
    },
    breakpoints: {
      "(max-width: 1024px)": {
        slides: { perView: 2.2, spacing: 16 },
      },
      "(max-width: 768px)": {
        slides: { perView: 1.2, spacing: 12 },
      },
    },
    detailsChanged: (slider) => {
      this.currentSlide = slider.track.details.rel;
      this.cdr.markForCheck();
    },
    created: () => {
      if (this.sliderRef.nativeElement.contains(document.activeElement)) {
        (document.activeElement as HTMLElement)?.blur();
      }

      this.updateSlidesPerView();
      this.cdr.markForCheck();
    },
    updated: () => {
      this.updateSlidesPerView();
      this.cdr.markForCheck();
    },
    optionsChanged: () => {
      this.updateSlidesPerView();
      this.cdr.markForCheck();
    },
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const scrollY = window.scrollY;

    requestAnimationFrame(() => {
      this.initSlider();

      setTimeout(() => {
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
      }, 0);
    });

    this.slidesContent.changes.subscribe(() => {
      this.scheduleUpdate();
    });
  }

  private initSlider(): void {
    if (typeof window === "undefined" || this.isInitialized) return;
    if (this.slidesContent.length === 0) return;

    try {
      const scrollY = window.scrollY;

      this.sliderRef.nativeElement.classList.add("keen-slider--pre-init");

      this.slider = new KeenSlider(
        this.sliderRef.nativeElement,
        this.ksOptions,
      );

      this.isInitialized = true;

      this.sliderRef.nativeElement.classList.remove("keen-slider--pre-init");
      this.sliderRef.nativeElement.classList.add("keen-slider--ready");

      if (window.scrollY !== scrollY) {
        window.scrollTo(0, scrollY);
      }
    } catch (error) {
      console.error("Error initializing slider:", error);
    }
  }

  private scheduleUpdate(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      const scrollY = window.scrollY;

      if (this.slider && this.isInitialized && this.slidesContent.length > 0) {
        this.slider.update(this.ksOptions);
      } else if (this.slidesContent.length > 0 && !this.isInitialized) {
        this.initSlider();
      }

      requestAnimationFrame(() => {
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
      });
    }, 100);
  }

  private updateSlidesPerView(): void {
    if (typeof window === "undefined") return;

    if (window.innerWidth <= 768) {
      this.slidesPerView = 1.2;
      return;
    }

    if (window.innerWidth <= 1024) {
      this.slidesPerView = 2.2;
      return;
    }

    this.slidesPerView = 4;
  }

  ngOnDestroy(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    if (this.slider) {
      this.slider.destroy();
      this.isInitialized = false;
    }
  }

  moveToSlide(idx: number): void {
    this.slider?.moveToIdx(idx);
  }

  prev(): void {
    this.slider?.prev();
  }

  next(): void {
    this.slider?.next();
  }

  get totalSlides(): number {
    return this.slidesContent.length;
  }

  get showArrows(): boolean {
    return this.totalSlides > Math.ceil(this.slidesPerView);
  }
}
